/**
 * Cross-platform browser notification helper.
 *
 * Desktop browsers (Chrome/Firefox/Edge/Safari): works from any tab.
 * iOS Safari 16.4+: requires the site to be installed as a PWA (Add to Home Screen).
 *                   Regular browser tabs on iOS CANNOT receive notifications.
 * Android Chrome: works from any tab.
 *
 * This helper:
 *   1. Detects support
 *   2. Requests permission
 *   3. Sends a notification with icon + body + tag (replaces duplicates)
 *   4. Returns an iOS-specific hint string when the user is on iOS and hasn't
 *      installed the PWA.
 */

const isIOS = () => /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;

const isStandalone = () =>
  window.matchMedia('(display-mode: standalone)').matches ||
  window.navigator.standalone === true;

export const isNotificationSupported = () => {
  if (typeof window === 'undefined') return false;
  if (!('Notification' in window)) return false;
  // On iOS, Notification API exists but only works inside an installed PWA.
  if (isIOS() && !isStandalone()) return false;
  return true;
};

export const getNotificationStatus = () => {
  if (!('Notification' in window)) return 'unsupported';
  if (isIOS() && !isStandalone()) return 'ios-pwa-required';
  return Notification.permission; // 'default' | 'granted' | 'denied'
};

export const requestNotificationPermission = async () => {
  if (!isNotificationSupported()) return getNotificationStatus();
  if (Notification.permission === 'granted') return 'granted';
  if (Notification.permission === 'denied') return 'denied';
  try {
    const res = await Notification.requestPermission();
    return res;
  } catch {
    return 'denied';
  }
};

/**
 * Fire a browser notification.
 * @param {Object} opts
 * @param {string} opts.title   — notification title
 * @param {string} opts.body    — body text
 * @param {string} [opts.tag]   — duplicate-replacement tag (e.g. 'strength-QQQ')
 * @param {string} [opts.icon]  — icon URL (defaults to /favicon.ico)
 * @param {Function} [opts.onClick] — click handler (focuses tab)
 * @returns {boolean} true if sent
 */
export const sendNotification = ({ title, body, tag, icon, onClick }) => {
  if (!isNotificationSupported()) return false;
  if (Notification.permission !== 'granted') return false;
  try {
    const n = new Notification(title, {
      body,
      tag: tag || 'stockpimp',
      icon: icon || '/favicon.ico',
      silent: false,
      requireInteraction: false,
    });
    n.onclick = (e) => {
      try {
        e.preventDefault();
        window.focus();
        if (onClick) onClick();
        n.close();
      } catch { /* silent */ }
    };
    return true;
  } catch {
    return false;
  }
};

export const getIOSInstallHint = () => {
  if (!isIOS()) return null;
  if (isStandalone()) return null;
  return 'On iPhone, push notifications require installing this site: tap Share → Add to Home Screen, then open it from the icon and enable alerts.';
};

// ==================== WEB PUSH ====================

const urlB64ToUint8Array = (base64String) => {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(base64);
  return Uint8Array.from([...raw].map((c) => c.charCodeAt(0)));
};

export const registerServiceWorker = async () => {
  if (!('serviceWorker' in navigator)) return null;
  try {
    // Register at root so it can intercept all routes
    const reg = await navigator.serviceWorker.register('/sw.js', { scope: '/' });
    return reg;
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('SW register failed', e);
    return null;
  }
};

/**
 * Subscribe the browser to Web Push and save the subscription on the backend.
 * @param {Object} opts
 * @param {string} opts.apiBase  — backend base URL (REACT_APP_BACKEND_URL)
 * @param {string[]} [opts.tickers] — tickers the user wants alerts for
 * @returns {Promise<PushSubscription|null>}
 */
export const subscribeToPush = async ({ apiBase, tickers = [] }) => {
  if (!('serviceWorker' in navigator) || !('PushManager' in window)) return null;
  const reg = await navigator.serviceWorker.ready;

  // Fetch VAPID public key
  const res = await fetch(`${apiBase}/api/push/vapid-public-key`);
  if (!res.ok) return null;
  const { publicKey } = await res.json();

  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlB64ToUint8Array(publicKey),
    });
  }

  // Save to backend
  const body = {
    subscription: sub.toJSON(),
    tickers,
  };
  await fetch(`${apiBase}/api/push/subscribe`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  return sub;
};

export const unsubscribeFromPush = async ({ apiBase }) => {
  if (!('serviceWorker' in navigator)) return;
  const reg = await navigator.serviceWorker.ready;
  const sub = await reg.pushManager.getSubscription();
  if (!sub) return;
  const endpoint = sub.endpoint;
  try { await sub.unsubscribe(); } catch { /* silent */ }
  try {
    await fetch(`${apiBase}/api/push/unsubscribe`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ endpoint }),
    });
  } catch { /* silent */ }
};

export const sendTestPush = async ({ apiBase }) => {
  if (!('serviceWorker' in navigator)) return false;
  const reg = await navigator.serviceWorker.ready;
  const sub = await reg.pushManager.getSubscription();
  if (!sub) return false;
  try {
    const res = await fetch(`${apiBase}/api/push/test`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ endpoint: sub.endpoint }),
    });
    return res.ok;
  } catch {
    return false;
  }
};
